/* HealthspanX / progressive product exploration. No commerce, tracking or form requests. */
(() => {
  'use strict';
  if (!customElements.get('hsx-product-explainer')) {
    customElements.define('hsx-product-explainer', class extends HTMLElement {
      connectedCallback() {
        if (this.controller) return;
        this.controller = new AbortController();
        const options = {signal: this.controller.signal};
        this.tabs = [...this.querySelectorAll('[data-hsxd-tab]')];
        this.panels = [...this.querySelectorAll('[data-hsxd-panel]')];
        this.tablist = this.querySelector('[data-hsxd-tabs]');
        if (!this.tablist || this.tabs.length !== this.panels.length) return;
        this.tablist.hidden = false;
        this.tablist.setAttribute('role', 'tablist');
        this.tabs.forEach((tab, index) => {
          tab.setAttribute('role', 'tab');
          this.panels[index].setAttribute('role', 'tabpanel');
          this.panels[index].setAttribute('aria-labelledby', tab.id);
          tab.addEventListener('click', () => this.select(index), options);
          tab.addEventListener('keydown', event => {
            let next = index;
            if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
            else if (event.key === 'ArrowLeft') next = (index + this.tabs.length - 1) % this.tabs.length;
            else if (event.key === 'Home') next = 0;
            else if (event.key === 'End') next = this.tabs.length - 1;
            else return;
            event.preventDefault(); this.select(next); this.tabs[next].focus();
          }, options);
        });
        this.dataset.enhanced = 'true'; this.select(0);
      }
      select(index) {
        this.tabs.forEach((tab, i) => {
          tab.setAttribute('aria-selected', String(i === index));
          tab.tabIndex = i === index ? 0 : -1;
          this.panels[i].hidden = i !== index;
        });
        const diagram = this.closest('.hsxd-science')?.querySelector('[data-hsxd-pathway]');
        if (diagram) diagram.dataset.mode = String(index + 1);
      }
      disconnectedCallback() { this.controller?.abort(); this.controller = null; }
    });
  }

  const initialized = new WeakSet();
  const openers = new WeakMap();
  function resetZoom(dialog) {
    dialog.removeAttribute('data-hsxd-magnified');
    const button = dialog.querySelector('[data-hsxd-magnify]');
    if (button) { button.setAttribute('aria-pressed', 'false'); button.textContent = button.dataset.zoomLabel; }
    const stage = dialog.querySelector('.hsxd-viewer__stage');
    if (stage) { stage.scrollTop = 0; stage.scrollLeft = 0; }
  }
  function initialize(scope = document) {
    if (!window.HTMLDialogElement?.prototype.showModal) return;
    scope.querySelectorAll('[data-hsxd-zoom]').forEach(button => { button.hidden = false; });
    scope.querySelectorAll('[data-hsxd-viewer]').forEach(dialog => {
      if (initialized.has(dialog)) return;
      initialized.add(dialog);
      dialog.addEventListener('close', () => {
        document.documentElement.classList.remove('hsxd-media-open');
        resetZoom(dialog);
        const opener = openers.get(dialog);
        if (opener?.isConnected) opener.focus({preventScroll: true});
        openers.delete(dialog);
      });
      dialog.addEventListener('click', event => { if (event.target === dialog) dialog.close(); });
    });
  }
  document.addEventListener('click', event => {
    const close = event.target.closest('[data-hsxd-viewer-close]');
    if (close) { close.closest('dialog')?.close(); return; }
    const magnify = event.target.closest('[data-hsxd-magnify]');
    if (magnify) {
      const dialog = magnify.closest('dialog');
      const zoomed = dialog.toggleAttribute('data-hsxd-magnified');
      magnify.setAttribute('aria-pressed', String(zoomed));
      magnify.textContent = zoomed ? magnify.dataset.fitLabel : magnify.dataset.zoomLabel;
      if (!zoomed) resetZoom(dialog);
      return;
    }
    const opener = event.target.closest('[data-hsxd-zoom],[data-hsxd-image]');
    if (!opener || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0 || !window.HTMLDialogElement?.prototype.showModal) return;
    const dialog = document.querySelector('[data-hsxd-viewer]');
    if (!dialog || document.querySelector('dialog[open]')) return;
    const source = opener.matches('[data-hsxd-zoom]') ? opener.closest('[data-product-stage]')?.querySelector('[data-product-image]') : null;
    const src = source?.currentSrc || source?.src || opener.href;
    if (!src) return;
    event.preventDefault();
    const image = dialog.querySelector('[data-hsxd-viewer-image]');
    image.src = src; image.alt = source?.alt || opener.dataset.hsxdAlt || '';
    image.hidden = false;
    resetZoom(dialog); openers.set(dialog, opener);
    dialog.showModal(); document.documentElement.classList.add('hsxd-media-open');
    dialog.querySelector('[data-hsxd-viewer-close]')?.focus();
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), {once: true});
  else initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => {
    if (event.target.querySelector('[data-hsxd-viewer][open]')) document.documentElement.classList.remove('hsxd-media-open');
  });
})();

(() => {
  'use strict';
  const bindings = new WeakMap();
  function enhance(scope = document) {
    scope.querySelectorAll('.hsxe-product').forEach(root => {
      if (bindings.has(root)) return;
      const controller = new AbortController();
      const options = {signal: controller.signal};
      const disposers = [() => controller.abort()];
      const native = root.querySelector('[data-product-plan]');
      const variant = root.querySelector('[data-product-variant]');
      let data;
      try { data = JSON.parse(root.querySelector('[data-product-data]').textContent); } catch { data = null; }
      if (native && variant && data) {
        const group = document.createElement('div');
        group.className = 'hsxe-plans'; group.setAttribute('role', 'group');
        const label = root.querySelector('label[for="' + native.id + '"]');
        if (label) { if (!label.id) label.id = native.id + '-label'; group.setAttribute('aria-labelledby', label.id); }
        native.insertAdjacentElement('afterend', group);
        let signature = '';
        const render = () => {
          const current = data.variants.find(item => String(item.id) === variant.value);
          if (!current) return;
          const choices = [...native.options];
          const next = variant.value + '|' + choices.map(option => option.value + ':' + option.textContent).join('|');
          if (next !== signature) {
            const focused = group.contains(document.activeElement) ? document.activeElement.dataset.hsxePlan : null;
            signature = next; group.replaceChildren();
            choices.forEach(option => {
              const plan = current.plans.find(item => String(item.id) === option.value);
              const button = document.createElement('button');
              button.type = 'button'; button.dataset.hsxePlan = option.value;
              const name = document.createElement('span'); name.textContent = plan?.name || option.textContent;
              const price = document.createElement('strong'); price.textContent = plan?.price || current.price;
              button.append(name, price); group.append(button);
            });
            if (focused !== null) [...group.children].find(button => button.dataset.hsxePlan === focused)?.focus();
          }
          [...group.children].forEach(button => button.setAttribute('aria-pressed', String(button.dataset.hsxePlan === native.value)));
        };
        group.addEventListener('click', event => {
          const button = event.target.closest('[data-hsxe-plan]');
          if (!button || !group.contains(button)) return;
          native.value = button.dataset.hsxePlan;
          native.dispatchEvent(new Event('change', {bubbles: true})); render();
        }, options);
        native.addEventListener('change', render, options);
        variant.addEventListener('change', () => queueMicrotask(render), options);
        const observer = new MutationObserver(render);
        observer.observe(native, {childList: true, subtree: true, characterData: true});
        disposers.push(() => observer.disconnect()); render(); native.hidden = true;
      }
      const track = root.querySelector('[data-hsxe-track]');
      const controls = root.querySelector('[data-hsxe-controls]');
      if (track && controls) {
        const tiles = [...track.children];
        const previous = controls.querySelector('[data-hsxe-previous]');
        const next = controls.querySelector('[data-hsxe-next]');
        const count = controls.querySelector('[data-hsxe-count]');
        let frame = 0;
        const current = () => {
          const start = track.getBoundingClientRect().left;
          let closest = 0, distance = Infinity;
          tiles.forEach((tile, index) => { const delta = Math.abs(tile.getBoundingClientRect().left - start); if (delta < distance) {distance = delta; closest = index;} });
          return closest;
        };
        const refresh = () => { frame = 0; const index = current(); count.textContent = (index + 1) + ' / ' + tiles.length; previous.disabled = index === 0; next.disabled = index === tiles.length - 1; };
        const move = delta => {
          const target = tiles[Math.min(tiles.length - 1, Math.max(0, current() + delta))];
          const left = target.getBoundingClientRect().left - track.getBoundingClientRect().left + track.scrollLeft;
          const still = matchMedia('(prefers-reduced-motion: reduce)').matches || document.documentElement.dataset.hsxMotion === 'false';
          track.scrollTo({left, behavior: still ? 'instant' : 'smooth'});
        };
        track.addEventListener('scroll', () => { if (!frame) frame = requestAnimationFrame(refresh); }, {...options, passive: true});
        previous.addEventListener('click', () => move(-1), options); next.addEventListener('click', () => move(1), options);
        addEventListener('resize', refresh, options); refresh(); controls.hidden = false;
        disposers.push(() => { if (frame) cancelAnimationFrame(frame); });
      }
      const info = root.querySelector('.hsx-product-info');
      if (info && typeof ResizeObserver === 'function') {
        const position = () => { const header = document.querySelector('.hsx-v2-header'); const top = Math.max(80, (header?.getBoundingClientRect().height || 64) + 32); info.style.setProperty('--hsxe-buy-top', Math.min(top, innerHeight - info.offsetHeight - 24) + 'px'); };
        const size = new ResizeObserver(position); size.observe(info); addEventListener('resize', position, options); position(); disposers.push(() => size.disconnect());
      }
      if ('IntersectionObserver' in window) {
        document.documentElement.classList.add('hsxe-navigation-ready');
        const nav = new IntersectionObserver(entries => {
          const entry = entries[0];
          document.documentElement.dataset.hsxeExploring = String(!entry.isIntersecting && entry.boundingClientRect.top < 0);
        }, {rootMargin: '-240px 0px 0px 0px'});
        nav.observe(root); disposers.push(() => {nav.disconnect(); document.documentElement.classList.remove('hsxe-navigation-ready'); delete document.documentElement.dataset.hsxeExploring;});
      }
      bindings.set(root, () => disposers.forEach(dispose => dispose()));
    });
  }
  document.addEventListener('shopify:section:load', event => enhance(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('.hsxe-product').forEach(root => bindings.get(root)?.()));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => enhance(), {once: true}); else enhance();
})();
